import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    LinearProgress,
    Alert,
    Pagination,
    Stack
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

const UploadFileComponent = ({
    uploadedFiles = [],
    selectedUploadedFile = '',
    onFileUpload,
    onFileSelect,
    onFileDelete,
    disabled = false,
    item = {},
    uploadUrl = '',
    maxUploadSize = '5 GB',
    uploadedSize = '0'
}) => {
    const fileInputRef = useRef(null);
    const dropAreaRef = useRef(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const filesPerPage = 5;

    // Helper function to parse size strings (e.g., "5.2 MB" -> bytes)
    const parseSizeToBytes = (sizeStr) => {
        if (!sizeStr || typeof sizeStr !== 'string') return 0;

        const units = { 'B': 1, 'KB': 1024, 'MB': 1024**2, 'GB': 1024**3, 'TB': 1024**4 };
        const match = sizeStr.trim().match(/^([\d.]+)\s*([A-Z]+)$/i);

        if (!match) return 0;

        const value = parseFloat(match[1]);
        const unit = match[2].toUpperCase();

        return Math.floor(value * (units[unit] || 1));
    };

    // Check if upload is disabled due to space limit
    const uploadedSizeBytes = parseSizeToBytes(uploadedSize);
    const maxUploadSizeBytes = parseSizeToBytes(maxUploadSize);
    const isUploadDisabledBySpace = uploadedSizeBytes >= maxUploadSizeBytes;
    const spaceUsagePercentage = maxUploadSizeBytes > 0 ? (uploadedSizeBytes / maxUploadSizeBytes) * 100 : 0;

    // Pagination logic
    const totalPages = Math.ceil(uploadedFiles.length / filesPerPage);
    const startIndex = (currentPage - 1) * filesPerPage;
    const endIndex = startIndex + filesPerPage;
    const currentFiles = uploadedFiles.slice(startIndex, endIndex);

    // Find which page contains the selected file
    const selectedFileIndex = uploadedFiles.findIndex(file => file.url === selectedUploadedFile);
    const selectedFilePage = selectedFileIndex >= 0 ? Math.floor(selectedFileIndex / filesPerPage) + 1 : 1;

    // Update current page to show selected file when selection changes
    useEffect(() => {
        if (selectedUploadedFile && selectedFilePage !== currentPage) {
            setCurrentPage(selectedFilePage);
        }
    }, [selectedUploadedFile, selectedFilePage]);

    // Initialize Resumable.js
    useEffect(() => {
        let resumable;
        const dropArea = dropAreaRef.current;
        if (typeof window !== 'undefined' && window.Resumable && !disabled && !isUploadDisabledBySpace && item.id) {
            // Get CSRF token
            const csrfToken = document.querySelector('input[name="_token"]')?.value;

            if (!csrfToken) {
                console.error('CSRF token not found');
                return;
            }

            resumable = new window.Resumable({
                target: uploadUrl,
                chunkSize: 1 * 1024 * 1024, // 1MB
                simultaneousUploads: 2,
                testChunks: false,
                throttleProgressCallbacks: 1,
                query: { _token: csrfToken },
                fileType: [
                    '3g2', '3gp', '3gp2', '3gpp', 'amv', 'asf', 'avi', 'divx', 'drc', 'dv',
                    'f4v', 'flv', 'gvi', 'gxf', 'ismv', 'iso', 'm1v', 'm2v', 'm2t', 'm2ts',
                    'm4v', 'mkv', 'mov', 'mp2', 'mp2v', 'mp4', 'mp4v', 'mpe', 'mpeg', 'mpeg1',
                    'mpeg2', 'mpeg4', 'mpg', 'mpv2', 'mts', 'mtv', 'mxf', 'mxg', 'nut', 'nuv',
                    'ogm', 'ogv', 'ogx', 'ps', 'rec', 'rm', 'rmvb', 'tod', 'ts', 'tts', 'vob',
                    'vro', 'webm', 'wm', 'wmv', 'wtv', 'xesc'
                ]
            });

            if (resumable.support) {
                // Assign drop functionality
                if (dropAreaRef.current) {
                    resumable.assignDrop(dropAreaRef.current);
                }

                // Assign browse functionality
                if (fileInputRef.current) {
                    resumable.assignBrowse(fileInputRef.current);
                }

                // Event handlers
                resumable.on('fileAdded', function (file) {
                    console.log('File added', file);

                    // Check if adding this file would exceed the limit
                    const estimatedNewSize = uploadedSizeBytes + file.size;
                    if (estimatedNewSize > maxUploadSizeBytes) {
                        alert(`Upload cancelled: File would exceed storage limit (${maxUploadSize})`);
                        return false;
                    }

                    setIsUploading(true);
                    resumable.upload();
                });

                resumable.on('progress', function () {
                    const progressValue = Math.floor(resumable.progress() * 100);
                    setUploadProgress(progressValue);
                    console.log('Upload progress', progressValue + '%');
                });

                resumable.on('complete', function () {
                    console.log('Upload complete');
                    setIsUploading(false);
                    setUploadProgress(0);
                });

                resumable.on('fileSuccess', function (file, response) {
                    const responseData = JSON.parse(response);
                    console.log("Upload successful:", responseData);

                    const newFile = {
                        name: responseData.name,
                        size: responseData.size,
                        url: responseData.name,
                        isExisting: false
                    };

                    // Call the parent callback to add the file
                    if (onFileUpload) {
                        onFileUpload([newFile], true);
                    }
                });

                resumable.on('fileError', function (file, message) {
                    console.error('Upload error:', message);
                    setIsUploading(false);
                    setUploadProgress(0);
                    alert('Upload failed: ' + message);
                });

            } else {
                console.error('Resumable.js not supported by this browser');
            }
        }

        // Cleanup
        return () => {
            if (resumable) {
                resumable.cancel();
                if (dropArea) {
                    resumable.unAssignDrop(dropArea);
                }
            }
        };
    }, [item.id, disabled, isUploadDisabledBySpace]);

    const handleUploadClick = () => {
        if (isUploadDisabledBySpace) {
            alert(`Upload disabled: Storage limit reached (${uploadedSize} / ${maxUploadSize})`);
            return;
        }

        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileDelete = async (fileUrl) => {
        if (!confirm('Are you sure you want to delete this file?')) {
            return;
        }

        const csrfToken = document.querySelector('input[name="_token"]')?.value;

        try {
            const response = await fetch(uploadUrl, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                },
                body: JSON.stringify({
                    fileName: fileUrl
                })
            });

            if (response.ok) {
                // Call parent callback to remove the file
                if (onFileDelete) {
                    onFileDelete(fileUrl);
                }
            } else {
                alert('Error deleting file.');
            }
        } catch (error) {
            console.error('Delete error:', error);
            alert('Error deleting file.');
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        if (isUploadDisabledBySpace) return;

        if (dropAreaRef.current) {
            dropAreaRef.current.style.backgroundColor = '#f44336';
            dropAreaRef.current.style.color = 'white';
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        if (dropAreaRef.current) {
            dropAreaRef.current.style.backgroundColor = '';
            dropAreaRef.current.style.color = '';
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (dropAreaRef.current) {
            dropAreaRef.current.style.backgroundColor = '';
            dropAreaRef.current.style.color = '';
        }
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const getSpaceUsageColor = () => {
        if (spaceUsagePercentage >= 100) return 'error';
        if (spaceUsagePercentage >= 80) return 'warning';
        return 'success';
    };

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                    File Upload Configuration
                </Typography>

                {/* Storage limit warning */}
                {isUploadDisabledBySpace && (
                    <Alert
                        severity="error"
                        icon={<WarningIcon />}
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="body2">
                            <strong>Storage limit reached!</strong> You cannot upload more files.
                            Current usage: {uploadedSize} / {maxUploadSize}
                        </Typography>
                    </Alert>
                )}

                {/* High storage usage warning */}
                {!isUploadDisabledBySpace && spaceUsagePercentage >= 80 && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="body2">
                            <strong>Warning:</strong> Storage space is {spaceUsagePercentage.toFixed(1)}% full.
                            Consider deleting unused files.
                        </Typography>
                    </Alert>
                )}

                {!disabled && (
                    <Box sx={{ mb: 2 }}>
                        <input
                            ref={fileInputRef}
                            accept="video/*"
                            style={{ display: 'none' }}
                            multiple
                            type="file"
                            disabled={isUploadDisabledBySpace}
                        />
                        <Box
                            ref={dropAreaRef}
                            onClick={handleUploadClick}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            sx={{
                                p: 2,
                                border: '2px dashed #ccc',
                                borderRadius: 1,
                                textAlign: 'center',
                                cursor: isUploadDisabledBySpace ? 'not-allowed' : 'pointer',
                                opacity: isUploadDisabledBySpace ? 0.6 : 1,
                                transition: 'all 0.3s ease',
                                '&:hover': !isUploadDisabledBySpace ? {
                                    borderColor: 'primary.main',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                } : {}
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                                {isUploadDisabledBySpace
                                    ? 'Upload disabled - Storage limit reached'
                                    : 'Drop files here or click to upload'
                                }
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Accepted formats: MP4, AVI, MOV, etc.
                            </Typography>
                            <Typography
                                variant="caption"
                                display="block"
                                color={getSpaceUsageColor() + '.main'}
                                sx={{ mt: 1, fontWeight: 'medium' }}
                            >
                                Space used: {uploadedSize} / {maxUploadSize} ({spaceUsagePercentage.toFixed(1)}%)
                            </Typography>
                        </Box>

                        {isUploading && (
                            <Box sx={{ mt: 2 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{ mb: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    Upload progress: {uploadProgress}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {uploadedFiles.length > 0 && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Uploaded Files ({uploadedFiles.length}):
                        </Typography>

                        <List dense>
                            {currentFiles.map((file, index) => {
                                const globalIndex = startIndex + index;
                                const isSelected = selectedUploadedFile === file.url;
                                const isOnSelectedPage = Math.floor(globalIndex / filesPerPage) + 1 === selectedFilePage;

                                return (
                                    <ListItem
                                        key={globalIndex}
                                        selected={isSelected}
                                        onClick={() => onFileSelect && onFileSelect(file.url)}
                                        sx={{
                                            cursor: 'pointer',
                                            border: isSelected ? '2px solid' : '1px solid',
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            backgroundColor: isSelected
                                                ? 'primary.light'
                                                : isOnSelectedPage && !isSelected
                                                    ? 'action.hover'
                                                    : 'transparent',
                                            borderRadius: 1,
                                            mb: 0.5,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: isSelected
                                                    ? 'primary.light'
                                                    : 'action.hover'
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {file.name}
                                                    {isSelected && (
                                                        <Chip
                                                            size="small"
                                                            label="Selected"
                                                            color="primary"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={file.size}
                                        />
                                        {!disabled && !file.isExisting && (
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleFileDelete(file.url);
                                                    }}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="small"
                                    showFirstButton
                                    showLastButton
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Showing {startIndex + 1}-{Math.min(endIndex, uploadedFiles.length)} of {uploadedFiles.length} files
                                    {selectedUploadedFile && (
                                        <span style={{ color: 'primary.main', fontWeight: 'medium' }}>
                                            {' '}• Selected file on page {selectedFilePage}
                                        </span>
                                    )}
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                )}

            </CardContent>
        </Card>
    );
};

export default UploadFileComponent;

